import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundary } from 'react-error-boundary';

// Components
import ProtectedRoute from './components/Auth/ProtectedRoute';
import AdminRoute from './components/Auth/AdminRoute';
import DashboardLayout from './components/Layout/DashboardLayout';

// Pages
import Home from './pages/Home';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ForgotPassword from './pages/Auth/ForgotPassword';
import Dashboard from './pages/Dashboard/Dashboard';
import Analytics from './pages/Dashboard/Analytics';
import SentimentAnalysis from './pages/Dashboard/SentimentAnalysis';
import Citations from './pages/Dashboard/Citations';
import Prompts from './pages/Dashboard/Prompts';
import Snippets from './pages/Dashboard/Snippets';
import Pages from './pages/Dashboard/Pages';
import EditPage from './pages/Dashboard/EditPage';
import Profile from './pages/Dashboard/Profile';
import TermsOfService from './pages/Legal/TermsOfService';
import PrivacyPolicy from './pages/Legal/PrivacyPolicy';
import TestPageEditor from './pages/TestPageEditor';

// Context
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { SnippetProvider } from './contexts/SnippetContext';

// Styles
import './styles/globals.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Error Fallback Component
const ErrorFallback = ({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Something went wrong</h3>
          <p className="mt-1 text-sm text-gray-500">
            {error.message || 'An unexpected error occurred'}
          </p>
          <div className="mt-6">
            <button
              onClick={resetErrorBoundary}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <SnippetProvider>
              <SocketProvider>
                <Router>
                <div className="App">
                  <Toaster position="top-right" />
                  <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={<Home />} />
                    <Route path="/test-editor" element={<TestPageEditor />} />
                    
                    {/* Legal Routes */}
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    
                    {/* Auth Routes */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    
                    {/* Protected Dashboard Routes */}
                    <Route element={<ProtectedRoute />}>
                      <Route path="/dashboard" element={<DashboardLayout><Dashboard /></DashboardLayout>} />
                      <Route path="/dashboard/snippets" element={<DashboardLayout><Snippets /></DashboardLayout>} />
                      <Route path="/dashboard/pages" element={<DashboardLayout><Pages /></DashboardLayout>} />
                      <Route path="/dashboard/pages/edit/:pageId" element={<DashboardLayout><EditPage /></DashboardLayout>} />
                      <Route path="/dashboard/profile" element={<DashboardLayout><Profile /></DashboardLayout>} />
                      <Route path="/dashboard/analytics" element={<DashboardLayout><Analytics /></DashboardLayout>} />
                      <Route path="/dashboard/sentiment" element={<DashboardLayout><SentimentAnalysis /></DashboardLayout>} />
                      <Route path="/dashboard/citations" element={<DashboardLayout><Citations /></DashboardLayout>} />
                      <Route path="/dashboard/prompts" element={<DashboardLayout><Prompts /></DashboardLayout>} />
                      <Route path="/dashboard/actions" element={<DashboardLayout><div className="p-6">Action Center - Coming Soon</div></DashboardLayout>} />
                      <Route path="/dashboard/explorer" element={<DashboardLayout><div className="p-6">Prompt Explorer - Coming Soon</div></DashboardLayout>} />
                      <Route path="/dashboard/topics" element={<DashboardLayout><div className="p-6">Topics and Prompts - Coming Soon</div></DashboardLayout>} />
                      <Route path="/dashboard/competitors" element={<DashboardLayout><div className="p-6">Competitors - Coming Soon</div></DashboardLayout>} />
                    </Route>
                    
                    {/* Admin Routes */}
                    <Route element={<AdminRoute />}>
                      <Route path="/admin" element={<div className="p-6">Admin Dashboard - Coming Soon</div>} />
                    </Route>
                    
                    {/* Catch all route */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </div>
              </Router>
            </SocketProvider>
          </SnippetProvider>
        </AuthProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App; 